<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Text, Select, Input } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { valueOutOfRange } from 'src/helpers/utilities';

	export let device: Device;

	const id = real_id(device.id);
	const settings = readDeviceSettings(id);
	const sensorConfig = settings?.['motion:0']?.sensor ?? {};

	const FREQUENCY_OPTIONS = [
		{ title: '61.1 GHz', value: 1 },
		{ title: '61.2 GHz', value: 2 },
		{ title: '61.3 GHz', value: 3 },
		{ title: '61.4 GHz', value: 4 },
		{ title: '61.25 GHz', value: 5 },
	];

	const POWER_OPTIONS = [
		{ title: '-34 dBm', value: 0 },
		{ title: '-31.5 dBm', value: 1 },
		{ title: '-25 dBm', value: 2 },
		{ title: '-18 dBm', value: 3 },
		{ title: '-11 dBm', value: 4 },
		{ title: '-5 dBm', value: 5 },
		{ title: '0 dBm', value: 6 },
		{ title: '4.5 dBm', value: 7 },
	];

	const PGA_GAIN_OPTIONS = [
		{ title: '10 dB', value: 0 },
		{ title: '15 dB', value: 1 },
		{ title: '20 dB', value: 2 },
		{ title: '25 dB', value: 3 },
		{ title: '30 dB', value: 4 },
		{ title: '35 dB', value: 5 },
		{ title: '40 dB', value: 6 },
		{ title: '45 dB', value: 7 },
		{ title: '50 dB', value: 8 },
	];

	const THRESHOLD_MIN = 0;
	const THRESHOLD_MAX = 4095;

	let frequency = sensorConfig?.frequency ?? 5;
	let power = sensorConfig?.power ?? 7;
	let pga_gain = sensorConfig?.pga_gain ?? 8;
	let threshold: number = sensorConfig?.threshold ?? 80;

	let cacheFrequency = frequency;
	let cachePower = power;
	let cachePgaGain = pga_gain;
	let cacheThreshold = threshold;
	let loading = false;

	const findOption = (options: typeof FREQUENCY_OPTIONS, val: number) =>
		options.find((o) => o.value === val) ?? options[0];

	$: frequencyValue = findOption(FREQUENCY_OPTIONS, frequency);
	$: powerValue = findOption(POWER_OPTIONS, power);
	$: pgaGainValue = findOption(PGA_GAIN_OPTIONS, pga_gain);

	$: thresholdError = valueOutOfRange(threshold, THRESHOLD_MIN, THRESHOLD_MAX)
		? $format('min_max_error_description', {
				values: { min: THRESHOLD_MIN, max: THRESHOLD_MAX },
				default: 'Value must be between {min} and {max}.',
			})
		: '';

	$: hasChanges =
		cacheFrequency !== frequency ||
		cachePower !== power ||
		cachePgaGain !== pga_gain ||
		cacheThreshold !== threshold;

	const save = async () => {
		if (!hasChanges || thresholdError) return;

		loading = true;
		const thresholdNum = Number(threshold);
		const params = {
			id: device.channel,
			config: {
				sensor: { frequency, power, pga_gain, threshold: thresholdNum },
			},
		};
		const res: SettingRequest = await gen2Request(device, 'Motion.SetConfig', params);
		const response = onSettingsResponse(res, device);

		if (response) {
			settings['motion:0'] = {
				...(settings['motion:0'] ?? {}),
				sensor: { frequency, power, pga_gain, threshold: thresholdNum },
			};
			devices_settings.update_devices_settings(id, settings);
			cacheFrequency = frequency;
			cachePower = power;
			cachePgaGain = pga_gain;
			cacheThreshold = threshold;
		} else {
			frequency = cacheFrequency;
			power = cachePower;
			pga_gain = cachePgaGain;
			threshold = cacheThreshold;
		}
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m">
	{$format('motion_sensor_settings_description', {
		default: 'Configure advanced sensor parameters.',
	})}
</Text>

<Text styleType="label-small" class="mb-xs">
	{$format('motion_frequency_label', { default: 'Frequency' })}
</Text>
<Select
	class="mb-m"
	options={FREQUENCY_OPTIONS}
	value={frequencyValue}
	on:change={(e) => (frequency = e.detail.value)}
	id="select-motion-frequency"
	aria-label={$format('motion_frequency_label', { default: 'Frequency' })}
/>

<Text styleType="label-small" class="mb-xs">
	{$format('motion_power_label', { default: 'Power' })}
</Text>
<Select
	class="mb-m"
	options={POWER_OPTIONS}
	value={powerValue}
	on:change={(e) => (power = e.detail.value)}
	id="select-motion-power"
	aria-label={$format('motion_power_label', { default: 'Power' })}
/>

<Text styleType="label-small" class="mb-xs">
	{$format('motion_pga_gain_label', { default: 'PGA Gain' })}
</Text>
<Select
	class="mb-m"
	options={PGA_GAIN_OPTIONS}
	value={pgaGainValue}
	on:change={(e) => (pga_gain = e.detail.value)}
	id="select-motion-pga-gain"
	aria-label={$format('motion_pga_gain_label', { default: 'PGA Gain' })}
/>

<Text styleType="label-small" class="mb-xs">
	{$format('motion_threshold_label', { default: 'Motion threshold' })}
</Text>
<Input
	class="mb-m"
	size="m"
	bind:value={threshold}
	type="number"
	min={THRESHOLD_MIN}
	max={THRESHOLD_MAX}
	error={thresholdError}
	icon="fas fa-sensor"
	id="input-motion-threshold"
	aria-labelledby="input-motion-threshold"
/>

<Button size="m" width="full" disabled={!hasChanges || !!thresholdError} {loading} on:click={save}>
	{$format('button_save', { default: 'Save' })}
</Button>
